import React, { createContext, useContext, useState, useEffect } from "react";
import { useListAgents, setAuthTokenGetter, setOnAuthError } from "@workspace/api-client-react";
import type { Agent } from "@workspace/api-client-react";
import { Capacitor } from "@capacitor/core";
import { initPushNotifications } from "@/lib/push-notifications";

const STORAGE_KEY = "waslacrm_agent";
const TOKEN_KEY = "waslacrm_token";

interface StoredAgent {
  id: number;
  name: string;
  email: string;
  role: string;
  color: string;
  avatarUrl: string | null;
  onlineStatus: string;
}

interface AgentContextType {
  currentAgent: Agent | null;
  storedAgent: StoredAgent | null;
  setCurrentAgentId: (id: number) => void;
  login: (agent: StoredAgent, token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export function AgentProvider({ children }: { children: React.ReactNode }) {
  const [storedAgent, setStoredAgent] = useState<StoredAgent | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Register token getter + global auth-error handler with the API client.
  // setOnAuthError fires whenever an authenticated request is rejected with 401
  // (token expired/invalid/revoked) — TanStack Query swallows those rejections,
  // so this fetch-layer hook is what reliably forces logout. The
  // unhandledrejection listener below is a backstop for any raw fetch() calls
  // that bypass the API client.
  useEffect(() => {
    setAuthTokenGetter(() => localStorage.getItem(TOKEN_KEY));
    setOnAuthError(() => logout());
    const handle = (e: PromiseRejectionEvent) => {
      if (e.reason?.status === 401) {
        logout();
      }
    };
    window.addEventListener("unhandledrejection", handle);
    return () => {
      setAuthTokenGetter(null);
      setOnAuthError(null);
      window.removeEventListener("unhandledrejection", handle);
    };
  }, []);

  const { data: agents } = useListAgents({ query: { enabled: !!storedAgent } as any });

  const [currentAgentId, setCurrentAgentId] = useState<number | null>(
    storedAgent?.id ?? null
  );

  useEffect(() => {
    if (agents && agents.length > 0 && !currentAgentId && storedAgent) {
      setCurrentAgentId(storedAgent.id);
    }
  }, [agents, storedAgent]);

  const agentsList = Array.isArray(agents) ? agents : [];
  const currentAgent = agentsList.find((a) => a.id === currentAgentId) || null;

  function login(agent: StoredAgent, token: string) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(agent));
    localStorage.setItem(TOKEN_KEY, token);
    setStoredAgent(agent);
    setCurrentAgentId(agent.id);
    if (Capacitor.isNativePlatform()) {
      initPushNotifications();
    }
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setStoredAgent(null);
    setCurrentAgentId(null);
  }

  return (
    <AgentContext.Provider value={{
      currentAgent,
      storedAgent,
      setCurrentAgentId,
      login,
      logout,
      isAuthenticated: !!storedAgent,
    }}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgent() {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error("useAgent must be used within an AgentProvider");
  }
  return context;
}
